import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, Loader2, AlertCircle } from "lucide-react";

import clubbieLogo from "../../assets/images/clubbie_logo_full.svg";
import TournamentBadge from "../../components/TournamentBadge/TournamentBadge";
import {
  getMemberInvitation,
  acceptMemberInvitation,
} from "../../redux/actions/usersActions";
import {
  setLocalStorageItem,
  removeLocalStorageItem,
  LOCAL_STORAGE_KEYS,
} from "../../utils/localStorage";

const ROLE_LABELS = {
  referee: "Referee",
  tournamentAdmin: "Administrator",
  divisionAdmin: "Division Administrator",
};

/**
 * Public Member Invitation Response Page
 * Allows users to accept tournament member invitations (referee/admin) via email link
 * Auth required to accept — redirects to login if not logged in
 */
const MemberInvitationResponse = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [invitationDetails, setInvitationDetails] = useState(null);
  const [accepted, setAccepted] = useState(false);

  const isLoggedIn = !!window.localStorage.getItem("access");

  // Accept the invitation (auth required)
  const handleAccept = useCallback(async () => {
    if (!isLoggedIn) {
      // Store token so we can redirect back after auth
      setLocalStorageItem(LOCAL_STORAGE_KEYS.PENDING_MEMBER_INVITATION, { token });
      navigate("/login");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const result = await acceptMemberInvitation(token);

      setAccepted(true);
      setInvitationDetails((prev) => ({
        ...prev,
        status: "APPROVED",
        canRespond: false,
        tournamentName: result.tournamentName || prev?.tournamentName,
      }));
    } catch (err) {
      setError(
        err.message ||
        "Failed to accept the invitation. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }, [isLoggedIn, token, navigate]);

  // Fetch invitation details on mount
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getMemberInvitation(token);
        setInvitationDetails(data);

        // Auto-accept if user just came back from login
        const pending = JSON.parse(
          window.localStorage.getItem(LOCAL_STORAGE_KEYS.PENDING_MEMBER_INVITATION) || "null"
        );
        if (pending?.token === token && isLoggedIn && data.canRespond) {
          removeLocalStorageItem(LOCAL_STORAGE_KEYS.PENDING_MEMBER_INVITATION);
          // Trigger accept after state settles
          setTimeout(() => handleAccept(), 0);
        }
      } catch (err) {
        setError(
          err.message ||
          "Unable to load invitation details. The link may be invalid or expired."
        );
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDetails();
    } else {
      setError("Invalid invitation link.");
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const roleLabel = ROLE_LABELS[invitationDetails?.role] || invitationDetails?.role || "Member";

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-lg w-full text-center">
          <div className="flex items-center justify-center mb-5">
            <img className="h-auto w-[160px]" src={clubbieLogo} alt="clubbie logo" />
          </div>
          <Loader2 className="w-10 h-10 text-[#00E7FF] animate-spin mx-auto mb-3" />
          <p className="text-app-text-secondary text-sm">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !invitationDetails) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-lg w-full text-center">
          <div className="flex items-center justify-center mb-5">
            <img className="h-auto w-[160px]" src={clubbieLogo} alt="clubbie logo" />
          </div>
          <AlertCircle className="w-12 h-12 text-[#FF7B7B] mx-auto mb-4" />
          <h1 className="text-[18px] font-semibold text-app-text mb-2">
            Unable to Load Invitation
          </h1>
          <p className="text-app-text-secondary text-sm mb-6">{error}</p>
          <div className="flex justify-center">
            <a
              href="/"
              className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all"
            >
              Go to Homepage
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Already responded state
  const normalizedStatus = invitationDetails?.status?.toLowerCase();
  const isAlreadyAccepted = normalizedStatus === "approved" || normalizedStatus === "accepted" || normalizedStatus === "accept";
  if (invitationDetails && !invitationDetails.canRespond && !accepted) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-lg w-full text-center">
          <div className="flex items-center justify-center mb-5">
            <img className="h-auto w-[160px]" src={clubbieLogo} alt="clubbie logo" />
          </div>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 bg-[#4AED88]/10 border border-[#4AED88]/30">
            <Check className="w-7 h-7 text-[#4AED88]" />
          </div>
          <h1 className="text-[18px] font-semibold text-app-text mb-2">
            Invitation already {isAlreadyAccepted ? "accepted" : "handled"}
          </h1>
          <p className="text-app-text-secondary text-sm mb-6">
            This invitation to join{" "}
            <span className="text-app-text">{invitationDetails.tournamentName}</span> as a{" "}
            <span className="text-app-text">{roleLabel}</span> has already been{" "}
            {isAlreadyAccepted ? "accepted" : "handled"}.
          </p>
          {isAlreadyAccepted && (
            <div className="flex justify-center">
              <button
                onClick={() => navigate("/")}
                className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all"
              >
                Go to dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Success state (just accepted)
  if (accepted) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-lg w-full text-center">
          <div className="flex items-center justify-center mb-5">
            <img className="h-auto w-[160px]" src={clubbieLogo} alt="clubbie logo" />
          </div>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 bg-[#4AED88]/10 border border-[#4AED88]/30">
            <Check className="w-7 h-7 text-[#4AED88]" />
          </div>
          <h1 className="text-[18px] font-semibold text-app-text mb-2">
            Invitation Accepted!
          </h1>
          <p className="text-app-text-secondary text-sm mb-6">
            You have been added to{" "}
            <span className="text-app-text">{invitationDetails?.tournamentName}</span> as a{" "}
            <span className="text-app-text">{roleLabel}</span>.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => navigate("/")}
              className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all"
            >
              Continue to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main invitation view
  return (
    <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
      <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-lg w-full">
        {/* Header */}
        <div className="text-center items-center justify-center mb-2">
          <div className="flex items-center justify-center mb-5">
            <img
              className="h-auto w-[160px]"
              src={clubbieLogo}
              alt="clubbie logo"
            />
          </div>
          <p className="text-app-text text-[18px] font-medium">
            {`You have been invited to join ${invitationDetails?.tournamentName}`}
          </p>
        </div>

        {/* Invitation Details Card */}
        <div className="flex items-start justify-center py-3">
          {/* Tournament Info */}
          <div className="flex flex-col min-w-0 items-center justify-start text-center gap-3 px-2">
            <TournamentBadge className="flex items-center justify-center p-2 w-12 h-12 rounded-full object-cover border-2 border-[#192634]" src={invitationDetails?.tournamentLogo} alt={invitationDetails?.tournamentName} />
            <div className="w-full">
              <h3 className="text-lg font-semibold text-app-text break-words">
                {invitationDetails?.tournamentName}
              </h3>
            </div>
          </div>
        </div>

        {/* Invitation message */}
        <div className="mb-6 text-center">
          <p className="text-app-text text-[15px]">
            {invitationDetails?.organizerName}{" "}
            <span className="text-app-text-secondary">has invited</span>{" "}
            {invitationDetails?.memberName || invitationDetails?.memberEmail}{" "}
            <span className="text-app-text-secondary">to join</span>{" "}
            {invitationDetails?.tournamentName}{" "}
            <span className="text-app-text-secondary">as a</span>{" "}
            {roleLabel}.
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-[#FF7B7B]/10 border border-[#FF7B7B]/30 rounded-lg p-4 mb-6">
            <p className="text-[#FF7B7B] text-sm">{error}</p>
          </div>
        )}

        {/* Expired notice */}
        {invitationDetails?.isExpired && (
          <div className="bg-[#FF7B7B]/10 border border-[#FF7B7B]/30 rounded-lg p-4 mb-6">
            <p className="text-[#FF7B7B] text-sm">
              This invitation has expired. Please contact the tournament organizer.
            </p>
          </div>
        )}

        {/* Action Button */}
        {!invitationDetails?.isExpired && invitationDetails?.canRespond && (
          <div className="flex gap-3">
            <button
              onClick={handleAccept}
              disabled={submitting}
              className="flex-1 bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : null}
              {isLoggedIn ? "Accept invitation" : "Login to accept"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberInvitationResponse;
